import { faComments } from '@fortawesome/free-regular-svg-icons';
import {
  faDownLeftAndUpRightToCenter,
  faTrashArrowUp,
  faUpRightAndDownLeftFromCenter,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Locked } from '@wfp/icons-react';
import { Tag, Text } from '@wfp/react';
import { formatDistanceToNow } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Element } from 'react-scroll';

import ConfirmationModal from 'src/components/ConfirmationModal';
import { CRF23 } from 'src/constants';
import { useIsMount } from 'src/hooks';
import {
  commentReplyRequestAPI,
  commentRequestAPI,
  commentRestoreRequestAPI,
} from 'src/redux/data/actions';
import {
  COMMENTS_LIST,
  REPLY,
  REPLY_DETAIL,
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/view/constants';
import {
  anchorsArrayFromNarrative,
  getDateWithTimezone,
  getUserInfo,
  sendGAEvent,
} from 'src/utils';
import CommentControlPanel from './CommentControlPanel';
import CommentsTextInput from './commentsTextInput';
import ReviewIcon from './ReviewIcon';

export const CommentListItemComponent = ({
  data,
  commentsContainerID,
  sectionAPIKwargs,
  props,
  id,
  nested = false,
  parentCommentID,
  issuesOverview = false,
  activeUser,
  permissions,
  parentClosed,
  highlightIsHovered,
  highlightComment,
}) => {
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(data?.closed || false);
  const [replyCommentContent, setReplyCommentContent] = useState('');
  const [newContent, setNewContent] = useState(data.content || '');
  const [highlightAnchor, setHighlightAnchor] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showCloseConfirmationModal, setCloseConfirmationModal] =
    useState(false);
  const [showRestoreConfirmationModal, setRestoreConfirmationModal] =
    useState(false);
  const [edit, setEdit] = useState(false);
  const [replyComment, setReplyComment] = useState(false);
  const [newTitle, setNewTitle] = useState(data.title || '');

  const appConfig = useSelector(
    state => state.viewData?.GET_APP_CONFIGURATION?.data,
  );
  const roles = appConfig?.userInfo?.roles;
  const isHqAdmin = roles && roles.includes('hq_admin');

  const isMount = useIsMount();

  // Check if any nested replies should be highlighted and expand parent if needed
  useEffect(() => {
    if (!nested && highlightComment && data?.comments && data?.closed) {
      const hasHighlightedReply = data.comments.some(
        reply => reply.pk.toString() === highlightComment,
      );

      if (hasHighlightedReply && collapsed) {
        setTimeout(() => {
          setCollapsed(false);
        }, 50);
      }
    }
  }, [highlightComment, data?.comments, nested, data?.closed, collapsed]);

  useEffect(() => {
    if (!isMount) {
      updateAnchorStyle();
    }
  }, [highlightAnchor]);

  function toggleEdit() {
    setEdit(!edit);
  }

  function toggleReplyComment() {
    setReplyComment(!replyComment);
  }

  function toggleDeleteConfirmationModal() {
    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
  }

  function toggleCloseConfirmationModal() {
    setCloseConfirmationModal(!showCloseConfirmationModal);
  }

  function toggleRestoreConfirmationModal() {
    setRestoreConfirmationModal(!showRestoreConfirmationModal);
  }

  function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  function updateAnchorStyle() {
    const elements = document.querySelectorAll(`[id='${data.anchor}']`);
    elements.forEach(element => {
      if (!element.classList.contains('comment-anchor')) return;

      if (highlightAnchor) {
        element.style.backgroundColor = '#e03e04';
      } else {
        element.style.backgroundColor = 'transparent';
      }
    });
  }

  function updateComment(requestData, method, refreshNarrative = false) {
    let refreshNarrativeType = '';

    if (refreshNarrative) {
      const narrativeArray = anchorsArrayFromNarrative(props.data.content);
      const isLinkedMainNarrative = narrativeArray.includes(data.anchor);

      if (isLinkedMainNarrative) {
        refreshNarrativeType = 'main';
      } else {
        let isLinkedExtraNarrative = false;

        props.data.extra_narratives.forEach(extraNarrative => {
          const extraNarrativeArray = anchorsArrayFromNarrative(
            extraNarrative.content,
          );

          if (extraNarrativeArray.includes(data.anchor))
            isLinkedExtraNarrative = true;
        });

        if (isLinkedExtraNarrative) refreshNarrativeType = 'extra';
      }

      if (props.is2023CRF) refreshNarrativeType = 'multi';
    }

    const params = {
      url_format: {
        ...sectionAPIKwargs,
        _comment_: data.pk,
      },
      method,
      data: requestData,
      storage_key: COMMENTS_LIST,
      reportId: props.reportId,
      refreshNarrative: refreshNarrativeType,
    };

    dispatch(commentRequestAPI(params));
  }

  function createReply() {
    toggleReplyComment();
    setReplyCommentContent('');

    const params = {
      url_format: {
        ...sectionAPIKwargs,
        _comment_: data.pk,
      },
      method: 'post',
      data: { content: replyCommentContent },
      storage_key: REPLY,
    };

    dispatch(commentReplyRequestAPI(params));
  }

  function updateExistingComment() {
    const requestData = { content: newContent };
    if (!nested) {
      requestData['title'] = newTitle;
      requestData['edit'] = true;
      updateComment(requestData, 'patch');
      toggleEdit();
    } else {
      toggleEdit();
      const params = {
        url_format: {
          ...sectionAPIKwargs,
          _comment_: parentCommentID,
          _reply_: data.pk,
        },
        method: 'patch',
        data: requestData,
        storage_key: REPLY_DETAIL,
      };
      dispatch(commentReplyRequestAPI(params));
    }
  }

  function deleteCommentReply() {
    toggleDeleteConfirmationModal();

    const params = {
      url_format: {
        ...sectionAPIKwargs,
        _comment_: parentCommentID,
        _reply_: data.pk,
      },
      method: 'delete',
      data: {},
      storage_key: REPLY_DETAIL,
    };

    dispatch(commentReplyRequestAPI(params));
  }

  function deleteComment() {
    setHighlightAnchor(false);
    updateComment({}, 'delete', true);
  }

  function markAsClosed() {
    setHighlightAnchor(false);
    updateComment({ closed: true }, 'patch', true);
    toggleCloseConfirmationModal();
  }

  function restoreComment() {
    dispatch(
      commentRestoreRequestAPI({
        url_format: {
          ...sectionAPIKwargs,
          _comment_: parentCommentID,
          _reply_: data.pk,
        },
        data: {
          comment_id: id,
        },
      }),
    );
    toggleRestoreConfirmationModal();
  }

  function toggleAsImportant() {
    updateComment({ important: !data?.important }, 'patch');
  }

  function updateReplyContent(e) {
    setReplyCommentContent(e.target.value);
  }

  function updateNewContent(e) {
    setNewContent(e.target.value);
  }

  function updateNewTitle(e) {
    setNewTitle(e.target.value);
  }

  function getDeleteCommentHeading() {
    const closingText = 'Do you really want to proceed?';
    const headingComment = `You are about to delete comment with ID ${data.pk}${
      data.comments?.length > 0 ? ' and all its replies.' : '.'
    } \n${closingText}`;
    const headingIssue = `You are about to delete reply with ID ${data.pk}. \n${closingText}`;

    return nested ? headingIssue : headingComment;
  }

  const nested_comments = data?.comments?.map((comment_data, index) => {
    const id = comment_data.pk;
    const isHighlighted =
      highlightComment && comment_data.pk.toString() === highlightComment;

    const props = {
      data: comment_data,
      key: index,
      id,
      parentClosed: data?.closed,
      parentCommentID: data?.pk,
      activeUser: activeUser,
      sectionAPIKwargs: !issuesOverview ? sectionAPIKwargs : null,
      commentsContainerID: !issuesOverview ? commentsContainerID : null,
      permissions: !issuesOverview ? permissions : {},
      nested: true,
      issuesOverview,
      highlightIsHovered: isHighlighted,
      highlightComment,
    };
    return <CommentListItem key={id} {...props} />;
  });

  const deleteMethod = !nested ? deleteComment : deleteCommentReply;

  // Create a fallback ID for scrolling when anchor is not available
  const elementId = data.anchor || `comment-${data.pk}`;

  return (
    <Element
      className={`${
        nested
          ? 'nested-comment'
          : data?.closed
          ? 'closed-comment'
          : data?.important
          ? 'important-comment'
          : ''
      }
          ${highlightIsHovered ? 'highlighted' : ''}
          acr-comment`}
      onMouseEnter={() => setHighlightAnchor(true)}
      onMouseLeave={() => setHighlightAnchor(false)}
      name={elementId}
      id={elementId}
      data-comment-id={data.pk}
      data-test-id="comment-item"
    >
      {!nested && (
        <div className="comment-cover">
          <div className="acr-comment-header">
            {data?.closed ? (
              <Locked
                width="30"
                height="30"
                color="grey"
                className="acr-comment-title acr-comment-title-icon"
              />
            ) : (
              <FontAwesomeIcon
                width="30"
                height="30"
                icon={faComments}
                className="acr-comment-title acr-comment-title-icon"
                color="grey"
              />
            )}
            <div className="acr-comment-title" data-test-id="comment-title">
              <Text className="acr-comment-title-text">{data.title}</Text>
              {collapsed && (
                <Text className="acr-comment-title__pk-part">
                  Comment ID: {data.pk}
                </Text>
              )}
            </div>
          </div>
          {data?.closed && (
            <FontAwesomeIcon
              onClick={() => setCollapsed(!collapsed)}
              width="20"
              height="20"
              icon={
                collapsed
                  ? faUpRightAndDownLeftFromCenter
                  : faDownLeftAndUpRightToCenter
              }
              className="acr-comment-title acr-comment-title-icon"
              fill="#000"
            />
          )}

          {!issuesOverview && !isEmpty(permissions) && (
            <ReviewIcon
              permissions={permissions}
              collapsed={collapsed}
              closed={data?.closed}
              user={data?.user}
              activeUser={activeUser}
              data={data}
              toggleCollapsed={toggleCollapsed}
              toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
              isReportPublished={props.reportIsPublished}
            />
          )}
        </div>
      )}

      <div className={collapsed ? 'collapsed-comment' : `comment-body`}>
        {data?.restored && (
          <Tag
            className="thread-restored-label"
            type="custom"
            data-test-id="restored-label"
          >
            Restored
          </Tag>
        )}
        {data?.closed ? (
          <Tag
            className="thread-closed-label"
            type="custom"
            data-test-id="closed-thread"
          >
            Closed
          </Tag>
        ) : data?.important ? (
          <Tag type="error" data-test-id="high-importance">
            High Importance
          </Tag>
        ) : (
          ''
        )}
        <div className="comment-info">
          <Text className="acr-comment-username">
            {getUserInfo(data.user)}
            <Text className="comment-body__pk-part">
              {nested ? 'Reply' : 'Comment'} ID: {data.pk}
            </Text>
          </Text>
          <Text className="comment-date">
            {getDateWithTimezone(data.date)}
            <br />
            {`${formatDistanceToNow(data.date.date)} ago`}
          </Text>
        </div>

        {!issuesOverview && (
          <>
            {showDeleteConfirmationModal && (
              <ConfirmationModal
                open={true}
                onSubmit={() => {
                  sendGAEvent('comment_delete_confirmation_btn_clicked');
                  deleteMethod();
                }}
                collapsed-comment
                onClose={toggleDeleteConfirmationModal}
                heading={getDeleteCommentHeading()}
                primaryButtonText="Yes"
                secondaryButtonText="No"
                customClassName="delete-comment-modal"
              />
            )}

            {showCloseConfirmationModal && (
              <ConfirmationModal
                open={true}
                onSubmit={markAsClosed}
                onClose={toggleCloseConfirmationModal}
                heading={`Do you really want to mark comment with ID ${data.pk} as closed?`}
                primaryButtonText="Yes"
                secondaryButtonText="No"
                customClassName="close-comment-modal"
              />
            )}

            {showRestoreConfirmationModal && (
              <ConfirmationModal
                open={true}
                onSubmit={restoreComment}
                onClose={toggleRestoreConfirmationModal}
                heading={`Do you really want to restore comment with ID ${data.pk}?`}
                primaryButtonText="Yes"
                secondaryButtonText="No"
                customClassName="restore-comment-modal"
              />
            )}
          </>
        )}

        <Text className="comment-content">{data.content}</Text>
        {!issuesOverview && !props.reportIsPublished && (
          <div className="acr-comment-control-panel">
            {isHqAdmin && data?.closed && (
              <span
                className="acr-comment-icon-holder"
                id="restore-comment"
                onClick={() => {
                  if (!props.isCommentsUpdating) {
                    toggleRestoreConfirmationModal();
                    return;
                  }
                  return null;
                }}
              >
                <FontAwesomeIcon
                  icon={faTrashArrowUp}
                  className="noselect issue-reply-icon"
                  height="16"
                  id={id}
                />
                <Text
                  className="acr-comment-icon-label"
                  data-test-id="restore-link"
                >
                  Restore
                </Text>
              </span>
            )}
            {!data?.closed && (
              <CommentControlPanel
                permissions={permissions}
                nested={nested}
                closed={data?.closed}
                user={data?.user}
                activeUser={activeUser}
                id={id}
                important={data?.important}
                parentClosed={parentClosed}
                toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
                toggleCloseConfirmationModal={toggleCloseConfirmationModal}
                toggleEdit={toggleEdit}
                toggleReplyComment={toggleReplyComment}
                markAsImportant={toggleAsImportant}
                isCommentsUpdating={props.isCommentsUpdating}
              />
            )}
          </div>
        )}
        {replyComment && !issuesOverview && (
          <CommentsTextInput
            displayTitleInput={false}
            contentValue={replyCommentContent}
            handleChange={updateReplyContent}
            submitButtonLabel="Post Reply"
            handleSubmit={() => {
              sendGAEvent('comment_post_reply_btn_clicked');
              createReply();
            }}
          />
        )}

        {edit && !issuesOverview && (
          <CommentsTextInput
            displayTitleInput={!nested}
            contentValue={newContent}
            titleValue={newTitle}
            handleChange={updateNewContent}
            handleChangeTitle={updateNewTitle}
            submitButtonLabel={`Update ${nested ? 'Reply' : 'Comment'}`}
            handleSubmit={updateExistingComment}
          />
        )}
        {nested_comments}
      </div>
    </Element>
  );
};

export const mapStateToProps = state => {
  return {
    props: {
      activeSectionID: 0,
      isCommentsUpdating: state.viewData[COMMENTS_LIST]?.isFetching || false,
      ...state.viewData[SECTION_DETAIL_DATA_KEY],
      ...state.viewState,
      is2023CRF: state.viewData[REPORT_DETAIL_DATA_KEY]?.data?.crf === CRF23,
      reportId: state.viewData[REPORT_DETAIL_DATA_KEY]?.data?.pk,
      reportIsPublished:
        state.viewData[REPORT_DETAIL_DATA_KEY]?.data?.state === 'Published',
    },
  };
};

const CommentListItem = connect(mapStateToProps)(CommentListItemComponent);
export default CommentListItem;
